import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  KeyRound,
  FileText,
  Database,
  Globe2,
  UserCheck,
  DownloadCloud,
  ArrowRight,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * Security & Trust section rendered on every public page (via PublicPageShell).
 * Content is app-owner editable copy describing enabled controls today.
 * Not a certification statement.
 */
export default function SecurityTrustSection() {
  const { t, lang } = useLang();
  const displayFont =
    lang === "bn"
      ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
      : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  const pillars = [
    {
      icon: Lock,
      en: "Encrypted in transit",
      bn: "ট্রান্সমিশনে এনক্রিপ্টেড",
      dEn: "All patient data moves over TLS between your browser, our backend, and the database.",
      dBn: "রোগীর সব ডেটা TLS-এর মাধ্যমে ব্রাউজার, ব্যাকএন্ড ও ডাটাবেসের মধ্যে চলাচল করে।",
    },
    {
      icon: Database,
      en: "Row-level access rules",
      bn: "রো-লেভেল অ্যাক্সেস",
      dEn: "Every table enforces per-clinic and per-role rules — a staff account cannot read another practice's records.",
      dBn: "প্রতিটি টেবিলে ক্লিনিক ও রোল অনুযায়ী নিয়ম — অন্য প্র্যাকটিসের রেকর্ড পড়া যায় না।",
    },
    {
      icon: KeyRound,
      en: "Modern authentication",
      bn: "আধুনিক অথেন্টিকেশন",
      dEn: "Email + password with optional 2-factor, Google sign-in, and secure token storage on desktop.",
      dBn: "ইমেইল + পাসওয়ার্ড, ঐচ্ছিক 2FA, Google সাইন-ইন এবং ডেস্কটপে সুরক্ষিত টোকেন স্টোরেজ।",
    },
    {
      icon: UserCheck,
      en: "Role-based staff access",
      bn: "রোল-ভিত্তিক স্টাফ অ্যাক্সেস",
      dEn: "Doctor, receptionist, and accountant roles each see only what their job needs.",
      dBn: "ডাক্তার, রিসেপশনিস্ট ও অ্যাকাউন্ট্যান্ট শুধু তাদের কাজের অংশ দেখেন।",
    },
    {
      icon: FileText,
      en: "Audit log",
      bn: "অডিট লগ",
      dEn: "Sensitive actions — prescription edits, exports, role changes — are timestamped and reviewable.",
      dBn: "সংবেদনশীল কার্যক্রম — Rx সম্পাদনা, এক্সপোর্ট, রোল পরিবর্তন — টাইমস্ট্যাম্প সহ রেকর্ড।",
    },
    {
      icon: DownloadCloud,
      en: "Your data, exportable",
      bn: "আপনার ডেটা, এক্সপোর্টযোগ্য",
      dEn: "One-click CSV/PDF export of patients, prescriptions, invoices — no lock-in, ever.",
      dBn: "রোগী, প্রেসক্রিপশন, ইনভয়েস এক-ক্লিকে CSV/PDF এক্সপোর্ট — কোনো লক-ইন নেই।",
    },
  ];

  const badges = [
    { en: "TLS 1.2+", bn: "TLS 1.2+" },
    { en: "Encrypted at rest", bn: "স্টোরেজে এনক্রিপ্টেড" },
    { en: "Bangladesh-hosted region option", bn: "বাংলাদেশ-হোস্টেড রিজিয়ন" },
    { en: "Daily managed backups", bn: "দৈনিক ব্যাকআপ" },
    { en: "GDPR-aligned data requests", bn: "GDPR-সদৃশ ডেটা রিকোয়েস্ট" },
    { en: "Bangla + English DPA on request", bn: "বাংলা + ইংরেজি DPA (রিকোয়েস্টে)" },
  ];

  return (
    <section
      aria-label={t("Security and trust", "নিরাপত্তা ও আস্থা")}
      className="border-t border-[#e2e8f0] bg-gradient-to-b from-white via-[#f8fafc] to-white"
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-[1fr,1.4fr] gap-10 items-start">
          {/* Left: headline */}
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-[#0f766e] bg-[#f0fdfa] border border-[#99f6e4] px-3 py-1.5 rounded-full mb-4">
              <ShieldCheck className="w-3.5 h-3.5" />
              {t("Security & trust", "নিরাপত্তা ও আস্থা")}
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#0c2340] leading-tight"
              style={{ fontFamily: displayFont }}
            >
              {t(
                "Patient records deserve hospital-grade care.",
                "রোগীর রেকর্ড হাসপাতাল-মানের যত্ন পাওয়ার যোগ্য।",
              )}
            </h2>
            <p className="mt-4 text-[15px] text-[#475569] leading-relaxed">
              {t(
                "This page is maintained by the DoctorsX team to explain the privacy, data handling, and access controls that are live in the product today. It is not an independent certification.",
                "এই পেজটি DoctorsX টিম কর্তৃক পরিচালিত এবং বর্তমানে সক্রিয় প্রাইভেসি, ডেটা হ্যান্ডলিং ও অ্যাক্সেস কন্ট্রোল বর্ণনা করে। এটি কোনো স্বাধীন সার্টিফিকেশন নয়।",
              )}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {badges.map((b) => (
                <span
                  key={b.en}
                  className="text-[11px] font-semibold text-[#0f766e] bg-[#f0fdfa] border border-[#99f6e4] px-2.5 py-1 rounded-full"
                >
                  {t(b.en, b.bn)}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/security"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0c2340] hover:bg-[#0f172a] px-4 py-2.5 rounded-full transition"
              >
                {t("Read the security overview", "নিরাপত্তা বিবরণ পড়ুন")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:security@doctorsx.com.bd"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0c2340] border border-[#cbd5e1] hover:border-[#0c2340] px-4 py-2.5 rounded-full transition"
              >
                <Globe2 className="w-4 h-4" />
                {t("Report a vulnerability", "দুর্বলতা রিপোর্ট করুন")}
              </a>
            </div>

            <p className="mt-4 text-[11px] text-[#94a3b8] leading-relaxed max-w-md">
              {t(
                "Shared responsibility: DoctorsX secures the platform; your clinic controls who is invited, what roles they hold, and how patient consent is captured.",
                "যৌথ দায়িত্ব: DoctorsX প্ল্যাটফর্মের নিরাপত্তা দেয়; আপনার ক্লিনিক ঠিক করে কে যুক্ত হবে, কে কোন রোল পাবে, ও রোগীর সম্মতি কীভাবে নেওয়া হবে।",
              )}
            </p>
          </div>

          {/* Right: pillars grid */}
          <div className="grid sm:grid-cols-2 gap-3">
            {pillars.map((p) => (
              <div
                key={p.en}
                className="rounded-2xl border border-[#e2e8f0] bg-white p-5 hover:border-[#0f766e] hover:-translate-y-0.5 transition"
              >
                <div className="w-9 h-9 rounded-xl bg-[#f0fdfa] border border-[#99f6e4] flex items-center justify-center mb-3">
                  <p.icon className="w-4 h-4 text-[#0f766e]" />
                </div>
                <div
                  className="text-[15px] font-bold text-[#0c2340]"
                  style={{ fontFamily: displayFont }}
                >
                  {t(p.en, p.bn)}
                </div>
                <p className="mt-1 text-[13px] text-[#64748b] leading-relaxed">
                  {t(p.dEn, p.dBn)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
